import {
  DEFAULT_FORM_VALUES,
  type PatchListRow,
  type TemplateId,
  type TtsRow,
  type VoiceCampaignFormValues,
} from './types';

/**
 * Map a raw `/api/obd/campaign/clone/{id}` response into the wizard's form
 * shape. The endpoint returns a free-form object — we permissively read each
 * field and fall back to the defaults when something is missing or wrongly
 * typed. Anything weird falls back gracefully so the form still renders.
 */
export function hydrateFromClonePayload(
  raw: unknown,
): Partial<VoiceCampaignFormValues> {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;

  const str = (v: unknown): string =>
    v === null || v === undefined ? '' : String(v);
  const num = (v: unknown, fallback = 0): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const json = <T,>(v: unknown, fallback: T): T => {
    if (typeof v !== 'string' || !v.trim()) return fallback;
    try {
      return JSON.parse(v) as T;
    } catch {
      return fallback;
    }
  };

  const rawTemplateId = str(r.templateId ?? r.template_id);
  let templateId: TemplateId = '0';
  if (
    rawTemplateId === '0' ||
    rawTemplateId === '1' ||
    rawTemplateId === '2' ||
    rawTemplateId === '3' ||
    rawTemplateId === '7' ||
    rawTemplateId === '8' ||
    rawTemplateId === '9' ||
    rawTemplateId === '10'
  ) {
    templateId = rawTemplateId as TemplateId;
  }

  const locationObj = json<{ locationList?: Array<string | number> }>(
    r.location,
    { locationList: [] },
  );
  const cliObj = json<{ cliList?: Array<string | number> }>(r.clis, {
    cliList: [],
  });
  /**
   * agentRows is `JSON.stringify({patchList: [...]})` per the legacy compose
   * wire format, but older campaigns may have stored a bare array. Handle
   * both shapes here so older saved data still hydrates.
   */
  const agentRows = (() => {
    const parsed = json<{ patchList?: PatchListRow[] } | PatchListRow[]>(
      r.agentRows,
      [],
    );
    if (Array.isArray(parsed)) return parsed;
    return Array.isArray(parsed.patchList) ? parsed.patchList : [];
  })();
  /** ttsRows wire field: `{ttsList: [...]}` (current) or bare array (older). */
  const ttsRows: TtsRow[] = (() => {
    const parsed = json<{ ttsList?: TtsRow[] } | TtsRow[]>(r.ttsRows, []);
    if (Array.isArray(parsed)) return parsed;
    return Array.isArray(parsed.ttsList) ? parsed.ttsList : [];
  })();

  const dtmf = (() => {
    const v = r.dtmf;
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === 'string' && v.trim()) {
      return v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  })();

  // Retry interval comes back in seconds; the form keeps minutes.
  const retryIntervalSec = num(r.retryInterval, 0);

  const welcomeId = str(r.welcomePId);
  const noinputId = str(r.noInputPId);
  const wronginputId = str(r.wrongInputPId);
  const thanksId = str(r.thanksPId);

  const patch: Partial<VoiceCampaignFormValues> = {
    campaignName: str(r.campaignName),
    templateId,
    sendLater: Boolean(r.scheduleTime),
    scheduleDate: str(r.scheduleTime),
    retries: num(r.retries, 0),
    retryInterval: Math.round(retryIntervalSec / 60),

    baseFileMode: r.baseId ? 'oldBaseFile' : DEFAULT_FORM_VALUES.baseFileMode,
    oldBaseId: r.baseId ? str(r.baseId) : '',

    /* Toggle flags derived from presence of the saved IDs. */
    useWelcomeFile: Boolean(welcomeId),
    useNoinputFile: Boolean(noinputId),
    useWronginputFile: Boolean(wronginputId),
    useThanksFile: Boolean(thanksId),

    welcomeId,
    menuId: str(r.menuPId),
    noinputId,
    wronginputId,
    thanksId,
    rePrompt: str(r.rePrompt),
    repeatMenu: str(r.repeatMenu),
    menuWaitTime: str(r.menuWaitTime),
    dtmf,
    patchList: agentRows,
    noAgentId: str(r.noAgentId),
    ivrId: str(r.ivrId),
    language: str(r.language),
    gender: str(r.gender),
    ttsRows,
    selectedTTSFlowId: str(r.ttsFlowId),

    locationIds: (locationObj.locationList ?? []).map(String),
    selectedCLI: (cliObj.cliList ?? []).map(String),

    sendSmsSuccess: Boolean(r.smsSuccessApi),
    smsApiSuccess: str(r.smsSuccessApi),
    callDurationSMS: num(r.callDurationSMS, 0),
    sendSmsFail: Boolean(r.smsFailApi),
    smsApiFail: str(r.smsFailApi),
    sendSmsDtmf: Boolean(r.smsDtmfApi),
    smsApiDtmf: str(r.smsDtmfApi),
    sendSmsPatchSuccess: Boolean(r.callPatchSuccessMessage),
    callPatchSuccessMessage: str(r.callPatchSuccessMessage),
    sendSmsPatchFail: Boolean(r.callPatchFailMessage),
    callPatchFailMessage: str(r.callPatchFailMessage),
    sendWebhook: Boolean(r.webhookId),
    webhookId: str(r.webhookId),
  };

  return patch;
}
